import React, { useState, useRef } from 'react';
import ModalDefault from '../../ModalDefault';
import ButtonDefault from '../../form/ButtonDefault';
import { IconClose, IconUpload } from '../../icons';
import { ModalWrapper, ModalTitle, ModalButtons } from '../../../pages/Demanda/styles';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { toastDefsError } from '../../../config/toastConfig';
import api from '../../../services/api';
import { comum } from '../../../styles/Themes/light';
import { X } from 'lucide-react';

const TextareaContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100% !important;
    max-width: 100%;
    box-sizing: border-box;

    label {
        font-size: 14px;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.interfaceText400};
    }

    textarea {
        width: 100%;
        max-width: 100%;
        min-height: 120px;
        padding: 12px;
        border: 1px solid ${({ theme }) => theme.colors.interfaceBorder100};
        border-radius: 6px;
        font-size: 14px;
        font-family: inherit;
        resize: vertical;
        transition: border-color 0.2s;
        box-sizing: border-box;

        &:focus {
            outline: none;
            border-color: ${({ theme }) => theme.colors.primaryRed400};
        }

        &::placeholder {
            color: ${({ theme }) => theme.colors.interfaceText200};
        }
    }

    .error {
        color: ${({ theme }) => theme.colors.primaryRed400};
        font-size: 12px;
        margin-top: 4px;
    }
`;

const UploadSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100% !important;

    .title {
        font-size: 14px;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.interfaceText400};
    }

    .upload-area {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        border: 2px dashed ${({ theme }) => theme.colors.interfaceBorder200};
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s;
        background: ${({ theme }) => theme.colors.interfacePure50};

        &:hover {
            border-color: ${({ theme }) => theme.colors.primaryRed400};
            background: ${({ theme }) => theme.colors.interfacePure100};
        }

        input[type="file"] {
            position: absolute;
            width: 100%;
            height: 100%;
            opacity: 0;
            cursor: pointer;
        }

        .upload-content {
            display: flex;
            align-items: center;
            gap: 8px;
            color: ${({ theme }) => theme.colors.interfaceText400};
            font-size: 14px;
        }
    }

    .files-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: 8px;

        .file-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 12px;
            background: ${({ theme }) => theme.colors.interfacePure100};
            border: 1px solid ${({ theme }) => theme.colors.interfaceBorder100};
            border-radius: 6px;
            font-size: 14px;
            color: ${({ theme }) => theme.colors.interfaceText400};

            .file-name {
                flex: 1;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .file-remove {
                cursor: pointer;
                padding: 4px;
                border-radius: 4px;
                transition: all 0.2s ease;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
                
                &:hover {
                    background: ${({ theme }) => theme.colors.interfaceText100};
                }
            }
        }
    }

    .uploading {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        background: ${({ theme }) => theme.colors.interfacePure100};
        border: 1px solid ${({ theme }) => theme.colors.interfaceBorder100};
        border-radius: 6px;
        font-size: 14px;
        color: ${({ theme }) => theme.colors.interfaceText400};

        .spinner {
            width: 16px;
            height: 16px;
            border: 2px solid ${({ theme }) => theme.colors.interfaceBorder200};
            border-top-color: ${({ theme }) => theme.colors.primaryRed400};
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
            to { transform: rotate(360deg); }
        }
    }
`;

const ContentWrapper = styled.div`
    width: 100% !important;
    box-sizing: border-box;

    > * {
        width: 100% !important;
        max-width: 100% !important;
    }
`;

const Disclaimer = styled.div`
    padding: 16px;
    background: ${({ theme }) => theme.colors.interfacePure100};
    border: 1px solid ${({ theme }) => theme.colors.interfaceBorder200};
    border-radius: 6px;
    margin-bottom: 24px;
    width: 100% !important;
    box-sizing: border-box;

    .bold {
        font-weight: 600;
        color: ${({ theme }) => theme.colors.interfaceText400};
        margin-bottom: 8px;

        span {
            color: ${({ theme }) => theme.colors.primaryRed400};
        }
    }

    p {
        font-size: 14px;
        color: ${({ theme }) => theme.colors.interfaceText300};
        line-height: 20px;
        margin: 0;
        margin-bottom: 0 !important;
    }
`;

interface ArquivoInterrupcao {
    id_proposta_arquivo?: number;
    caminho?: string;
    nome: string;
    file?: File;
}

interface ModalInterromperDemandaProps {
    modalAtivo: boolean;
    setModalAtivo: (ativo: boolean) => void;
    onConfirmar: (motivo: string, arquivosIds: number[]) => Promise<void>;
    loading?: boolean;
    idDemanda: number;
    onVoltar?: () => void;
}

export default function ModalInterromperDemanda({ 
    modalAtivo, 
    setModalAtivo, 
    onConfirmar,
    loading = false,
    idDemanda,
    onVoltar
}: ModalInterromperDemandaProps) {
    const [motivo, setMotivo] = useState('');
    const [erro, setErro] = useState('');
    const [arquivos, setArquivos] = useState<ArquivoInterrupcao[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const inputArquivoRef = useRef<HTMLInputElement>(null);

    const handleSelecionarArquivo = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const MAX_FILE_BYTES = 50 * 1024 * 1024;
        if (file.size > MAX_FILE_BYTES) {
            toast.error(`Arquivo "${file.name}" excede 50MB.`, toastDefsError);
            e.target.value = '';
            return;
        }

        const novoArquivo: ArquivoInterrupcao = {
            nome: file.name,
            file: file
        };

        setArquivos(prev => [...prev, novoArquivo]);
        
        if (inputArquivoRef.current) {
            inputArquivoRef.current.value = '';
        }
    };

    const handleConfirmar = async () => {
        const motivoTrimmed = motivo.trim();
        
        if (!motivoTrimmed) {
            setErro('O motivo da interrupção é obrigatório');
            return;
        }

        setErro('');
        setIsUploading(true);

        try {
            const arquivosIds: number[] = [];

            for (const arquivo of arquivos) {
                if (arquivo.file) {
                    try {
                        const formData = new FormData();
                        formData.append('arquivo', arquivo.file);
                        formData.append('tipo_acao', 'INTERROMPER');
                        formData.append('tipo_arquivo', 'interrupcao');

                        const response = await api.post(`proposta/${idDemanda}/upload-conclusao`, formData);

                        if (response.data.status !== 'success') {
                            throw new Error(response.data.message || 'Erro ao enviar arquivo');
                        }

                        if (response.data.result?.id_proposta_arquivo) {
                            arquivosIds.push(response.data.result.id_proposta_arquivo);
                        }
                    } catch (error: any) {
                        console.error('Erro ao enviar arquivo:', error);
                        const status = error?.response?.status;
                        const mensagemApi = error?.response?.data?.message || error?.response?.data?.details;
                        const mensagemDuplicado = 'Já existe um arquivo com esse nome para esta demanda. Renomeie o arquivo ou escolha outro.';
                        const mensagemAmigavel = status === 409 && mensagemApi
                            ? mensagemDuplicado
                            : mensagemApi || error?.message || 'Erro desconhecido';
                        toast.error(`Falha ao enviar "${arquivo.nome}": ${mensagemAmigavel}`, toastDefsError);
                        setIsUploading(false);
                        return;
                    }
                } else if (arquivo.id_proposta_arquivo) {
                    arquivosIds.push(arquivo.id_proposta_arquivo);
                }
            }

            await onConfirmar(motivoTrimmed, arquivosIds);
            setIsUploading(false);
        } catch (error: any) {
            console.error('Erro ao processar arquivos:', error);
            toast.error(error.message || 'Erro ao processar arquivos', toastDefsError);
            setIsUploading(false);
        }
    };

    const handleRemoverArquivo = (index: number) => {
        setArquivos(prev => prev.filter((_, i) => i !== index));
    };

    const handleCancelar = () => {
        setMotivo('');
        setErro('');
        setArquivos([]);
        setModalAtivo(false);
    };

    const handleVoltar = () => {
        setMotivo('');
        setErro('');
        setArquivos([]);
        setModalAtivo(false);
        if (onVoltar) {
            onVoltar();
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMotivo(e.target.value);
        if (erro && e.target.value.trim()) {
            setErro('');
        }
    };

    return (
        <ModalDefault
            id={'overlayModal'}
            onClose={handleCancelar}
            openModal={modalAtivo}
            width="600px"
            maxWidth="90vw"
        >
            <ModalWrapper>
                <ModalTitle>
                    <div className="points">
                        Interromper demanda
                    </div>
                    <div
                        className="close"
                        onClick={handleCancelar}
                    >
                        <IconClose />
                    </div>
                </ModalTitle>

                <ContentWrapper className="accept">
                    <Disclaimer>
                        <div className="bold">
                            Na interrupção da demanda, consideramos o serviço como não realizado, sem direito ao pagamento pelo ato contratado. Tem certeza que deseja interromper?
                        </div>
                        <p>Só interrompa a demanda caso haja tempo hábil para recontratação.</p>
                    </Disclaimer>

                    <TextareaContainer>
                        <label>
                            Motivo da interrupção <span style={{ color: 'var(--ce52940)' }}>*</span>
                        </label>
                        <textarea
                            placeholder="Escreva aqui o motivo da interrupção"
                            value={motivo}
                            onChange={handleChange}
                            disabled={loading || isUploading}
                        />
                        {erro && <div className="error">{erro}</div>}
                    </TextareaContainer>

                    <UploadSection>
                        <div className="title">Anexos relacionados (opcional):</div>
                        <div className="upload-area">
                            <input
                                ref={inputArquivoRef}
                                type="file"
                                onChange={handleSelecionarArquivo}
                                disabled={isUploading}
                            />
                            <div className="upload-content">
                                <IconUpload color={comum.colors.interfaceText400} />
                                <span>Clique para fazer upload</span>
                            </div>
                        </div>

                        {arquivos.length > 0 && (
                            <div className="files-list">
                                {arquivos.map((arquivo, index) => (
                                    <div key={index} className="file-item">
                                        <span className="file-name">{arquivo.nome}</span>
                                        <div
                                            className="file-remove"
                                            onClick={() => handleRemoverArquivo(index)}
                                            title="Remover arquivo"
                                        >
                                            <X size={16} color={comum.colors.interfaceDanger400} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </UploadSection>
                </ContentWrapper>

                <ModalButtons>
                    <ButtonDefault
                        className="cancel"
                        onClick={handleVoltar}
                        disabled={loading || isUploading}
                    >
                        Voltar
                    </ButtonDefault>
                    <ButtonDefault
                        className="danger"
                        onClick={handleConfirmar}
                        disabled={loading || isUploading || !motivo.trim()}
                    >
                        {loading || isUploading ? 'Processando...' : 'Interromper'}
                    </ButtonDefault>
                </ModalButtons>
            </ModalWrapper>
        </ModalDefault>
    );
}

